import { useEffect, useState } from "react";
import { ScrollText, Search, Filter } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const ENTITIES = ["all", "users", "services", "articles", "testimonials", "faqs", "team", "enquiries", "settings"];

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function actionColour(action = "") {
  if (action.startsWith("delete") || action.includes("inactive")) return "bg-red-100 text-red-700";
  if (action.startsWith("create")) return "bg-emerald-100 text-emerald-700";
  if (action.startsWith("status") || action.includes("active")) return "bg-amber-100 text-amber-700";
  return "bg-slate-100 text-slate-700";
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [entity, setEntity] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = {};
    if (entity !== "all") params.entity = entity;
    if (q) params.q = q;
    api.get("/admin/audit", { params }).then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entity]);

  return (
    <div data-testid="audit-log-page">
      <div className="flex items-center gap-2">
        <ScrollText className="text-gold" size={24} />
        <h1 className="font-serif text-3xl font-bold text-navy">Audit Trail</h1>
      </div>
      <p className="mt-1 text-sm text-slate-500">Immutable record of important actions, access and permission changes. Entries cannot be edited or deleted.</p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by user, action or detail…" className="pl-9" />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-slate-400" />
          <select data-testid="audit-entity-filter" value={entity} onChange={(e) => setEntity(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-navy">
            {ENTITIES.map((en) => <option key={en} value={en}>{en === "all" ? "All areas" : en}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Area</th>
              <th className="px-4 py-3">Detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : rows.length ? rows.map((r) => (
              <tr key={r.id} data-testid="audit-row" className="hover:bg-slate-50">
                <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmt(r.at)}</td>
                <td className="px-4 py-3 font-medium text-navy">{r.user}{r.user_role ? <span className="ml-1 text-xs text-slate-400">({r.user_role})</span> : null}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${actionColour(r.action)}`}>{r.action}</span></td>
                <td className="px-4 py-3 text-slate-600">{r.entity}</td>
                <td className="px-4 py-3 text-slate-500">{r.summary || "—"}</td>
              </tr>
            )) : (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No audit entries found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
